import {
  Box,
  Flex,
  Grid,
  Tooltip,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React from 'react';

import type { Block } from 'types/api/block';

import config from 'configs/app';
import getBlockTotalReward from 'lib/block/getBlockTotalReward';
import getNetworkValidatorTitle from 'lib/networks/getNetworkValidatorTitle';
import Skeleton from 'ui/shared/chakra/Skeleton';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import BlockEntity from 'ui/shared/entities/block/BlockEntity';
import IconSvg from 'ui/shared/IconSvg';
import TimeAgoWithTooltip from 'ui/shared/TimeAgoWithTooltip';

type Props = {
  block: Block;
  isLoading?: boolean;
};

const LatestBlocksItem = ({ block, isLoading }: Props) => {
  const totalReward = getBlockTotalReward(block);
  const textColor = useColorModeValue('black', 'white');

  const { colorMode } = useColorMode();
  return (
    <Box
      as={ motion.div }
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ display: 'none' }}
      transitionDuration="normal"
      transitionTimingFunction="linear"
      borderRadius="md"
      border={ `0.5px solid ${
        colorMode === 'dark' ? 'rgba(255,255,255,0.20)' : 'rgba(19,19,19,0.20)'
      }` }
      p={ 3 }
    >
      <Flex alignItems="center" overflow="hidden" w="100%" mb={ 3 }>
        <BlockEntity
          isLoading={ isLoading }
          number={ block.height }
          tailLength={ 2 }
          fontSize="xl"
          lineHeight={ 7 }
          fontWeight="normal"
          mr="auto"
        />
        { block.celo?.is_epoch_block && (
          <Tooltip label={ `Finalized epoch #${ block.celo.epoch_number }` }>
            <IconSvg
              name="checkered_flag"
              boxSize={ 5 }
              p="1px"
              ml={ 2 }
              isLoading={ isLoading }
              flexShrink={ 0 }
            />
          </Tooltip>
        ) }
        <TimeAgoWithTooltip
          timestamp={ block.timestamp }
          enableIncrement={ !isLoading }
          isLoading={ isLoading }
          color="text_secondary"
          fontWeight={ 400 }
          display="inline-block"
          fontSize="sm"
          flexShrink={ 0 }
          ml={ 2 }
        />
      </Flex>
      <Grid gridGap={ 2 } templateColumns="auto minmax(0, 1fr)" fontSize="sm">
        <Skeleton isLoaded={ !isLoading } color={ textColor }>
          Txn
        </Skeleton>
        <Skeleton isLoaded={ !isLoading } color="text_secondary">
          <span>{ block.transaction_count }</span>
        </Skeleton>

        { !config.features.rollup.isEnabled &&
          !config.UI.views.block.hiddenFields?.total_reward && (
          <>
            <Skeleton isLoaded={ !isLoading } color={ textColor }>
              Reward
            </Skeleton>
            <Skeleton isLoaded={ !isLoading } color="text_secondary">
              <span>{ totalReward.dp(10).toFixed() }</span>
            </Skeleton>
          </>
        ) }

        { !config.features.rollup.isEnabled &&
          !config.UI.views.block.hiddenFields?.miner && (
          <>
            <Skeleton
              isLoaded={ !isLoading }
              textTransform="capitalize"
              color={ textColor }
            >
              { getNetworkValidatorTitle() }
            </Skeleton>
            <AddressEntity
              address={ block.miner }
              isLoading={ isLoading }
              noIcon
              noCopy
              truncation="constant"
            />
          </>
        ) }
      </Grid>
    </Box>
  );
};

export default LatestBlocksItem;
